<template>
  <div class="absolute inset-0 z-50">
    <Button
      variant="ghost"
      size="sm"
      class="absolute bottom-2 right-2 bg-background/80 backdrop-blur-sm z-50 hover:bg-background/90"
      @click="isOpen = !isOpen"
    >
      <Icon :name="isOpen ? 'heroicons:eye-slash' : 'heroicons:eye'" class="w-4 h-4 mr-2" />
      {{ isOpen ? 'Hide Text' : 'Show Text' }}
    </Button>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <div
        v-if="isOpen && embeddedText"
        class="absolute bottom-14 right-2 max-w-sm p-4 rounded-lg bg-background/80 backdrop-blur-sm shadow-lg z-50"
      >
        <p class="text-sm">{{ embeddedText }}</p>
      </div>
      <div
        v-else-if="isOpen && isLoading"
        class="absolute bottom-14 right-2 p-4 rounded-lg bg-background/80 backdrop-blur-sm shadow-lg z-50"
      >
        <div class="flex items-center space-x-2">
          <Icon name="heroicons:arrow-path" class="w-4 h-4 animate-spin" />
          <span class="text-sm">Loading text...</span>
        </div>
      </div>
      <div
        v-else-if="isOpen && !embeddedText"
        class="absolute bottom-14 right-2 p-4 rounded-lg bg-background/80 backdrop-blur-sm shadow-lg z-50"
      >
        <p class="text-sm text-muted-foreground">No text available for this image</p>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useEmbeddingsStore } from '@/stores/embeddings'
import { Button } from '@/components/ui/button'
import { useLogger } from '@/composables/useLogger'

const logger = useLogger()

const props = defineProps<{
  bubbleId: string
  imagePath: string
}>()

const isOpen = ref(false)
const embeddingsStore = useEmbeddingsStore()
const isLoading = computed(() => embeddingsStore.loading[props.bubbleId] ?? false)

const embeddedText = computed(() => {
  const text = embeddingsStore.getEmbeddingForImage(props.bubbleId, props.imagePath)
  logger.debug('🔍 EmbeddedText: Computed text', {
    bubbleId: props.bubbleId,
    imagePath: props.imagePath,
    text: text ? text.substring(0, 50) + '...' : null,
    hasText: !!text,
    isLoading: isLoading.value,
  })
  return text
})

// Fetch embeddings when component is first rendered in full-screen
const hasFetched = ref(false)

watch(
  () => props.bubbleId,
  async (newBubbleId) => {
    if (newBubbleId && !hasFetched.value) {
      logger.debug('🔍 EmbeddedText: Fetching embeddings for new bubble', {
        bubbleId: newBubbleId,
        imagePath: props.imagePath,
        hasFetched: hasFetched.value,
      })
      hasFetched.value = true
      const result = await embeddingsStore.fetchEmbeddings(newBubbleId)
      logger.debug('🔍 EmbeddedText: Fetch result', {
        success: result.success,
        error: result.error,
        embeddings: result.embeddings?.length,
      })
    }
  },
  { immediate: true }
)

watch(isOpen, (newValue) => {
  logger.debug('🔍 EmbeddedText: Toggle visibility', {
    isOpen: newValue,
    bubbleId: props.bubbleId,
    imagePath: props.imagePath,
    hasText: !!embeddedText.value,
    isLoading: isLoading.value,
    hasFetched: hasFetched.value,
  })
})
</script>
